import React from 'react'
import {Box, Stack, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {GatewayEnvironment} from './types'

export default function EnvironmentBadge({
  environment,
  size = 'md',
}: {
  environment: GatewayEnvironment | null | undefined
  size?: 'sm' | 'md'
}) {
  const {t} = useTranslation()
  if (!environment) {
    return null
  }

  const isLive = environment === 'live'
  const fontSize = size === 'sm' ? 11 : 12
  const px = size === 'sm' ? '8px' : '10px'
  const py = size === 'sm' ? '2px' : '4px'

  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px,
        py,
        borderRadius: '6px',
        fontSize,
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        border: '1px solid',
        ...(isLive
          ? {
              color: '#9A3412',
              backgroundColor: '#FFF7ED',
              borderColor: '#FDBA74',
            }
          : {
              color: '#334155',
              backgroundColor: '#F1F5F9',
              borderColor: '#CBD5E1',
            }),
      }}
    >
      {isLive ? (t('payment.env_live') as string) : (t('payment.env_sandbox') as string)}
    </Box>
  )
}

export function EnvironmentSegment({
  value,
  onChange,
  disabled,
}: {
  value: GatewayEnvironment
  onChange: (next: GatewayEnvironment) => void
  disabled?: boolean
}) {
  const {t} = useTranslation()

  return (
    <Stack
      direction='row'
      sx={{
        display: 'inline-flex',
        p: '3px',
        borderRadius: '8px',
        backgroundColor: '#F1F5F9',
        border: '1px solid #E2E8F0',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {(['sandbox', 'live'] as GatewayEnvironment[]).map((env) => {
        const active = value === env
        const isLive = env === 'live'
        return (
          <Box
            key={env}
            component='button'
            type='button'
            disabled={disabled}
            onClick={() => onChange(env)}
            sx={{
              border: 0,
              cursor: disabled ? 'not-allowed' : 'pointer',
              px: '14px',
              py: '6px',
              borderRadius: '6px',
              fontSize: 13,
              fontWeight: 600,
              backgroundColor: active ? (isLive ? '#FFF7ED' : '#FFFFFF') : 'transparent',
              color: active ? (isLive ? '#9A3412' : '#0F172A') : '#64748B',
              boxShadow: active ? '0 1px 2px rgba(15,23,42,0.08)' : 'none',
            }}
          >
            <Typography component='span' fontSize={13} fontWeight={600}>
              {isLive ? (t('payment.env_live') as string) : (t('payment.env_sandbox') as string)}
            </Typography>
          </Box>
        )
      })}
    </Stack>
  )
}
